import { visibleSpanSeconds } from "./scales/visibleSpan";

const VISIBLE_RANGE_FIELDS = [
  "first",
  "last",
  "barSpacing",
  "rightOffset",
  "isLastBarVisible",
  "spanSeconds",
  "priceAxisWidth",
];
const CROSSHAIR_FIELDS = ["index", "price", "bar"];

const isSame = (previous, next, fields) =>
  previous === next ||
  Boolean(previous && next && fields.every((field) => previous[field] === next[field]));

// Panes are compared by their numbers, so a frame without layout changes tells nobody
const paneLayoutSignature = (layout) =>
  [
    layout.mainHeight,
    layout.areaHeight,
    layout.contentHeight,
    layout.scrollTop,
    layout.paneWidth,
    layout.priceAxisWidth,
    ...layout.panes.map(({ id, top, height, isExpanded }) => `${id}:${top}:${height}:${isExpanded}`),
  ].join("|");

const isSamePaneLayout = (previous, next) =>
  Boolean(previous) && paneLayoutSignature(previous) === paneLayoutSignature(next);

/** @param {string[] | ((previous: object|null, next: object) => boolean)} fields - compared fields or a comparison */
const createChannel = (fields) => {
  const listeners = new Set();
  const isSameValue = typeof fields === "function" ? fields : (previous, next) => isSame(previous, next, fields);
  let lastValue = null;

  return {
    get lastValue() {
      return lastValue;
    },

    // A new listener gets the current value at once: React subscribes after the first frames
    subscribe: (listener) => {
      listeners.add(listener);

      if (lastValue) listener(lastValue);

      return () => listeners.delete(listener);
    },
    emit: (value) => {
      if (isSameValue(lastValue, value)) return;

      lastValue = value;
      listeners.forEach((listener) => listener(value));
    },
  };
};

/**
 * Public events of the chart view (engine.events). Listeners are called
 * with the current value when they subscribe, then only when the value
 * really changed.
 */
export const createViewEvents = () => {
  const visibleRange = createChannel(VISIBLE_RANGE_FIELDS);
  const crosshair = createChannel(CROSSHAIR_FIELDS);
  const paneLayout = createChannel(isSamePaneLayout);

  return {
    /**
     * After a frame in which pan, zoom, a new bar or older history changed
     * what is visible.
     *
     * @param {(range: {
     *   first: number, last: number, barSpacing: number, rightOffset: number,
     *   isLastBarVisible: boolean, spanSeconds: number, priceAxisWidth: number,
     * }) => void} listener - first / last: indexes of visible bars in engine.data.store.bars;
     *   rightOffset: bars between the last bar and the price axis (negative when it is out of view);
     *   spanSeconds: time from the left edge to the price axis (0 while unknown);
     *   priceAxisWidth: CSS px
     * @returns {() => void} unsubscribes
     */
    onVisibleRangeChange: visibleRange.subscribe,

    /**
     * When the crosshair moves to another bar or price, appears or hides,
     * or the bar under it gets a tick.
     *
     * @param {(crosshair: {
     *   x: number, y: number, index: number, time: number, price: number, bar: object|null,
     *   paneId: string, decimals: number,
     * } | null) => void} listener - null when hidden; x, y in CSS px of the chart;
     *   bar is null over the empty space right of the last bar; price: on the scale of the pane
     *   under the pointer (paneId: "main" or the key of an oscillator); decimals: price precision of the pair
     * @returns {() => void} unsubscribes
     */
    onCrosshairMove: crosshair.subscribe,

    /**
     * When the oscillator panes move: added, removed, expanded, scrolled or resized (D24).
     *
     * @param {(layout: {
     *   mainHeight: number, areaHeight: number, contentHeight: number, scrollTop: number,
     *   paneWidth: number, priceAxisWidth: number,
     *   panes: Array<{ id: string, top: number, height: number, isExpanded: boolean }>,
     * }) => void} listener - CSS px of the chart: the area of the panes starts at mainHeight
     *   (the price pane height) and is areaHeight tall; contentHeight: all panes, scrolled by
     *   scrollTop when taller than the area; top of a pane: after the scroll; id: the indicator key
     * @returns {() => void} unsubscribes
     */
    onPaneLayoutChange: paneLayout.subscribe,

    /** @returns {object|null} the last value of onPaneLayoutChange, null before the first frame */
    getPaneLayout: () => paneLayout.lastValue,

    /** @param {{ pane: object, paneArea: object, indicatorPanes: object[], priceAxisWidth: number }} layout - frame */
    emitPaneLayout: ({ pane, paneArea, indicatorPanes, priceAxisWidth }) =>
      paneLayout.emit({
        mainHeight: paneArea.top,
        areaHeight: paneArea.height,
        contentHeight: paneArea.contentHeight,
        scrollTop: paneArea.scrollTop,
        paneWidth: pane.width,
        priceAxisWidth,
        panes: indicatorPanes.map(({ id, top, height, isExpanded }) => ({ id, top, height, isExpanded })),
      }),

    /**
     * @param {object} timeScale
     * @param {{ range: { first: number, last: number }, priceAxisWidth: number }} layout - frame
     * @param {number} barDurationMs
     */
    emitVisibleRange: (timeScale, { range, priceAxisWidth }, barDurationMs) =>
      visibleRange.emit({
        first: range.first,
        last: range.last,
        barSpacing: timeScale.barSpacing,
        rightOffset: timeScale.rightOffset,
        isLastBarVisible: timeScale.isLastBarVisible(),
        spanSeconds: visibleSpanSeconds(timeScale, barDurationMs),
        priceAxisWidth,
      }),

    /**
     * @param {object|null} value - see resolveCrosshair
     * @param {number} decimals - price precision of the pair
     */
    emitCrosshair: (value, decimals) =>
      crosshair.emit(value && { ...value, decimals }),
  };
};
